import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';

// Hook to detect mobile devices
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent));
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return isMobile;
};

// Emoji/SVG fallbacks for mobile to avoid WebGL performance issues
const MobileFallbackIcon: React.FC<{ type: string }> = ({ type }) => {
    const iconMap: Record<string, string> = {
        'dashboard': '📊',
        'projects': '📁',
        'dispatch': '📡',
        'skills': '⚡',
        'workspace': '💻',
        'academy': '🎓',
        'talent': '👥',
        'client': '🤝',
        '3dhub': '🧊',
        'aihub': '🤖',
        'financial': '💰',
        'remote': '🏠',
        'onsite': '📍',
        'urgent': '🚨'
    };

    return (
        <div className="w-full h-full flex items-center justify-center text-6xl md:text-7xl">
            {iconMap[type] || '⭐'}
        </div>
    );
};

// ThreeIconProps defined at end of file to support all variants


// Gold material for the premium look
const GoldMaterial = () => (
    <meshStandardMaterial
        color="#FFD700"
        roughness={0.1}
        metalness={0.8}
        emissive="#B8860B"
        emissiveIntensity={0.2}
    />
);

// Glass material for modern touch
const GlassMaterial = () => (
    <meshPhysicalMaterial
        color="#ffffff"
        roughness={0}
        metalness={0.1}
        transmission={0.9}
        thickness={0.5}
        transparent
        opacity={0.8}
    />
);

// Dispatch Icon - Broadcasting waves
const DispatchIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.z = state.clock.elapsedTime * 0.2;
        }
    });

    return (
        <group ref={group}>
            {/* Central broadcast tower/antenna */}
            <mesh position={[0, -0.5, 0]}>
                <coneGeometry args={[0.3, 1, 4]} />
                <GoldMaterial />
            </mesh>
            <mesh position={[0, 0.3, 0]}>
                <cylinderGeometry args={[0.08, 0.08, 0.8, 8]} />
                <GoldMaterial />
            </mesh>
            <mesh position={[0, 0.8, 0]}>
                <sphereGeometry args={[0.15, 16, 16]} />
                <meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={1} />
            </mesh>

            {/* Broadcasting waves (rings) */}
            {[0.5, 0.8, 1.1].map((radius, i) => (
                <mesh key={i} rotation={[Math.PI / 2, 0, 0]} position={[0, 0.3, 0]}>
                    <torusGeometry args={[radius, 0.04, 16, 32]} />
                    <meshStandardMaterial
                        color="#FFD700"
                        emissive="#FFD700"
                        emissiveIntensity={0.5 - i * 0.15}
                        transparent
                        opacity={0.7 - i * 0.2}
                    />
                </mesh>
            ))}
        </group>
    );
};

const DashboardIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.2;
        }
    });

    return (
        <group ref={group}>
            <mesh position={[-0.8, -0.5, 0]}>
                <boxGeometry args={[0.5, 1, 0.5]} />
                <GoldMaterial />
            </mesh>
            <mesh position={[0, 0, 0]}>
                <boxGeometry args={[0.5, 2, 0.5]} />
                <GoldMaterial />
            </mesh>
            <mesh position={[0.8, 0.5, 0]}>
                <boxGeometry args={[0.5, 3, 0.5]} />
                <GoldMaterial />
            </mesh>
        </group>
    );
};

const ProjectsIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.y = state.clock.elapsedTime * 0.2;
        }
    });

    return (
        <group ref={group}>
            {[-1, 0, 1].map((offset) => (
                <mesh key={offset} position={[offset * 0.2, offset * 0.3, offset * 0.2]} rotation={[0.2, 0, 0]}>
                    <boxGeometry args={[1.5, 0.1, 1.2]} />
                    <GoldMaterial />
                </mesh>
            ))}
            <mesh position={[0, 0, 0]}>
                <boxGeometry args={[0.5, 0.5, 0.5]} />
                <GlassMaterial />
            </mesh>
        </group>
    );
};

const SkillsIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.x = state.clock.elapsedTime * 0.5;
            group.current.rotation.y = state.clock.elapsedTime * 0.3;
        }
    });

    return (
        <group ref={group}>
            <mesh>
                <icosahedronGeometry args={[0.8, 0]} />
                <meshStandardMaterial color="#FFD700" wireframe />
            </mesh>
            <mesh>
                <sphereGeometry args={[0.4, 16, 16]} />
                <GoldMaterial />
            </mesh>
        </group>
    );
};

const WorkspaceIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
        }
    });

    return (
        <group ref={group}>
            <mesh>
                <boxGeometry args={[2, 1.2, 0.1]} />
                <meshStandardMaterial color="#333" roughness={0.2} metalness={0.8} />
            </mesh>
            <mesh position={[0, -0.8, 0]}>
                <boxGeometry args={[0.5, 0.5, 0.1]} />
                <GoldMaterial />
            </mesh>
            <mesh position={[0, -1.1, 0]}>
                <boxGeometry args={[1, 0.1, 0.5]} />
                <GoldMaterial />
            </mesh>
        </group>
    );
};

const AcademyIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.y = state.clock.elapsedTime * 0.2;
        }
    });

    return (
        <group ref={group}>
            <mesh position={[0, 0, 0]} rotation={[0, 0, 0]}>
                <coneGeometry args={[0.8, 1.5, 4]} />
                <GoldMaterial />
            </mesh>
            <mesh position={[0, -0.8, 0]}>
                <cylinderGeometry args={[1, 1, 0.2, 32]} />
                <GlassMaterial />
            </mesh>
        </group>
    );
};

const TalentIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.z = state.clock.elapsedTime * 0.1;
        }
    });

    return (
        <group ref={group}>
            <mesh position={[0, 0.8, 0]}>
                <sphereGeometry args={[0.3, 16, 16]} />
                <GoldMaterial />
            </mesh>
            <mesh position={[-0.7, -0.5, 0]}>
                <sphereGeometry args={[0.3, 16, 16]} />
                <GoldMaterial />
            </mesh>
            <mesh position={[0.7, -0.5, 0]}>
                <sphereGeometry args={[0.3, 16, 16]} />
                <GoldMaterial />
            </mesh>
            {/* Connectors */}
            <mesh position={[-0.35, 0.15, 0]} rotation={[0, 0, -0.5]}>
                <capsuleGeometry args={[0.05, 1.2, 4]} />
                <GlassMaterial />
            </mesh>
            <mesh position={[0.35, 0.15, 0]} rotation={[0, 0, 0.5]}>
                <capsuleGeometry args={[0.05, 1.2, 4]} />
                <GlassMaterial />
            </mesh>
            <mesh position={[0, -0.5, 0]} rotation={[0, 0, 1.57]}>
                <capsuleGeometry args={[0.05, 1.4, 4]} />
                <GlassMaterial />
            </mesh>
        </group>
    )
}

const ClientIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.5) * 0.2;
            group.current.rotation.y = state.clock.elapsedTime * 0.3;
        }
    });

    return (
        <group ref={group}>
            <mesh rotation={[Math.PI / 2, 0, 0]} position={[-0.3, 0, 0]}>
                <torusGeometry args={[0.5, 0.1, 16, 32]} />
                <GoldMaterial />
            </mesh>
            <mesh rotation={[Math.PI / 2, 0, 0]} position={[0.3, 0, 0]}>
                <torusGeometry args={[0.5, 0.1, 16, 32]} />
                <GlassMaterial />
            </mesh>
        </group>
    );
};

const ThreeHubIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.x = state.clock.elapsedTime * 0.3;
            group.current.rotation.y = state.clock.elapsedTime * 0.5;
        }
    });

    return (
        <group ref={group}>
            <mesh>
                <torusKnotGeometry args={[0.6, 0.2, 64, 8]} />
                <GoldMaterial />
            </mesh>
        </group>
    );
};

const AIHubIcon = () => {
    const group = useRef<THREE.Group>(null);
    const brain = useRef<THREE.Mesh>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.y = state.clock.elapsedTime * 0.3;
        }
        if (brain.current) {
            brain.current.rotation.x = Math.sin(state.clock.elapsedTime) * 0.1;
        }
    });

    return (
        <group ref={group}>
            {/* Robot head/brain */}
            <mesh ref={brain}>
                <sphereGeometry args={[0.6, 32, 32]} />
                <GoldMaterial />
            </mesh>
            {/* Eyes */}
            <mesh position={[-0.2, 0.1, 0.5]}>
                <sphereGeometry args={[0.08, 16, 16]} />
                <meshStandardMaterial color="#00ffff" emissive="#00ffff" emissiveIntensity={0.8} />
            </mesh>
            <mesh position={[0.2, 0.1, 0.5]}>
                <sphereGeometry args={[0.08, 16, 16]} />
                <meshStandardMaterial color="#00ffff" emissive="#00ffff" emissiveIntensity={0.8} />
            </mesh>
            {/* Antenna */}
            <mesh position={[0, 0.8, 0]}>
                <cylinderGeometry args={[0.02, 0.02, 0.4, 8]} />
                <GlassMaterial />
            </mesh>
            <mesh position={[0, 1.0, 0]}>
                <sphereGeometry args={[0.08, 16, 16]} />
                <meshStandardMaterial color="#FFD700" emissive="#FFD700" emissiveIntensity={1} />
            </mesh>
            {/* Circuit pattern rings */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[0.7, 0.03, 16, 32]} />
                <meshStandardMaterial color="#00ffff" emissive="#00ffff" emissiveIntensity={0.5} />
            </mesh>
            <mesh rotation={[0, 0, Math.PI / 2]}>
                <torusGeometry args={[0.75, 0.02, 16, 32]} />
                <meshStandardMaterial color="#00ffff" emissive="#00ffff" emissiveIntensity={0.3} />
            </mesh>
        </group>
    );
};

const FinanceIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.y = state.clock.elapsedTime * 0.5;
        }
    });

    return (
        <group ref={group}>
            {/* Stack of Coins */}
            {[0, 0.2, 0.4, 0.6].map((y, i) => (
                <mesh key={i} position={[0, y - 0.3, 0]} rotation={[Math.PI / 2, 0, 0]}>
                    <cylinderGeometry args={[0.5, 0.5, 0.15, 32]} />
                    <GoldMaterial />
                </mesh>
            ))}
            {/* Leaning Coin */}
            <mesh position={[0.4, 0.2, 0.4]} rotation={[Math.PI / 3, 0, Math.PI / 6]}>
                <cylinderGeometry args={[0.5, 0.5, 0.15, 32]} />
                <GoldMaterial />
            </mesh>
            {/* $ Symbol (Toruses abstract representation) */}
            <mesh position={[0, 1, 0]}>
                <sphereGeometry args={[0.2, 16, 16]} />
                <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.5} />
            </mesh>
        </group>
    );
};

// Remote Icon (House)
const RemoteIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
        }
    });

    return (
        <group ref={group}>
            {/* House body */}
            <mesh position={[0, -0.5, 0]}>
                <boxGeometry args={[1, 1, 1]} />
                <GoldMaterial />
            </mesh>
            {/* Roof */}
            <mesh position={[0, 0.5, 0]} rotation={[0, Math.PI / 4, 0]}>
                <coneGeometry args={[0.9, 0.8, 4]} />
                <GoldMaterial />
            </mesh>
            {/* Window/Door */}
            <mesh position={[0, -0.6, 0.51]}>
                <boxGeometry args={[0.3, 0.5, 0.05]} />
                <GlassMaterial />
            </mesh>
        </group>
    );
};

// On-Site Icon (Pin)
const OnSiteIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            // Bob up and down
            group.current.position.y = Math.sin(state.clock.elapsedTime * 2) * 0.2;
        }
    });

    return (
        <group ref={group}>
            {/* Pin head */}
            <mesh position={[0, 0.5, 0]}>
                <sphereGeometry args={[0.6, 16, 16]} />
                <meshStandardMaterial color="#FF4500" emissive="#FF4500" emissiveIntensity={0.5} />
            </mesh>
            {/* Pin point */}
            <mesh position={[0, -0.5, 0]}>
                <coneGeometry args={[0.2, 1.5, 16]} />
                <GoldMaterial />
            </mesh>
        </group>
    );
};

// Urgent Icon (Alarm/Lightning)
const UrgentIcon = () => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            // Shake/Vibrate effect
            group.current.rotation.z = Math.sin(state.clock.elapsedTime * 15) * 0.1;
            // Pulse scale
            const s = 1 + Math.sin(state.clock.elapsedTime * 8) * 0.1;
            group.current.scale.set(s, s, s);
        }
    });

    return (
        <group ref={group}>
            {/* Bell/Alarm body */}
            <mesh>
                <sphereGeometry args={[0.6, 16, 16]} />
                <meshStandardMaterial color="#FF0000" emissive="#FF0000" emissiveIntensity={0.8} />
            </mesh>
            {/* Bell clapper */}
            <mesh position={[0, -0.8, 0]}>
                <sphereGeometry args={[0.2, 8, 8]} />
                <GoldMaterial />
            </mesh>
        </group>
    );
};

interface ThreeIconProps {
    type: 'dashboard' | 'projects' | 'skills' | 'workspace' | 'academy' | 'talent' | 'client' | '3dhub' | 'aihub' | 'dispatch' | 'financial' | 'remote' | 'onsite' | 'urgent';
}

const ThreeIcon: React.FC<ThreeIconProps> = ({ type }) => {
    const isMobile = useIsMobile();

    // On mobile, use lightweight emoji fallbacks instead of heavy WebGL
    if (isMobile) {
        return <MobileFallbackIcon type={type} />;
    }

    // Render 3D icons immediately without lazy loading for instant display
    return (
        <div className="w-full h-full relative" style={{ minHeight: '100px', minWidth: '100px' }}>
            <Canvas
                dpr={[1, 1.5]}
                gl={{
                    antialias: false,
                    powerPreference: "high-performance",
                    alpha: true,
                    stencil: false,
                    depth: true
                }}
                camera={{ fov: 50, position: [0, 0, 4] }}
                frameloop="demand" // Only render when needed, not continuous
            >
                <ambientLight intensity={0.6} />
                <directionalLight position={[5, 5, 5]} intensity={0.8} />
                <pointLight position={[-5, -5, -5]} intensity={0.3} color="#FFD700" />

                {/* Removed expensive Environment preset for faster loading */}

                <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
                    {type === 'dashboard' && <DashboardIcon />}
                    {type === 'projects' && <ProjectsIcon />}
                    {type === 'skills' && <SkillsIcon />}
                    {type === 'workspace' && <WorkspaceIcon />}
                    {type === 'academy' && <AcademyIcon />}
                    {type === 'talent' && <TalentIcon />}
                    {type === 'client' && <ClientIcon />}
                    {type === '3dhub' && <ThreeHubIcon />}
                    {type === 'aihub' && <AIHubIcon />}
                    {type === 'dispatch' && <DispatchIcon />}
                    {type === 'financial' && <FinanceIcon />}
                    {type === 'remote' && <RemoteIcon />}
                    {type === 'onsite' && <OnSiteIcon />}
                    {type === 'urgent' && <UrgentIcon />}
                </Float>
            </Canvas>
        </div>
    );
};

export default ThreeIcon;
